// Bid Tracker report: writing the month's report as a workbook.
//
// The sheets come from utils/bidReportExport.js, the same definitions the
// Report tab's tables are drawn from; this file only lays them out (one sheet
// per section plus a Summary, a bold shaded header row, money as pounds and
// win rates as percentages) and hands the file to the browser, the way the
// PLOW export in ../delivery/plowExportSheet.js does. Written by
// utils/xlsxWriter.js (pen test AD25996, AD17).
import { downloadXlsx, colName } from '../../utils/xlsxWriter.js';
import { buildReportSheets, reportFileName } from '../../utils/bidReportExport.js';

const BORDER = { style: 'thin', color: { rgb: 'FFD1D5DB' } };
const border = { top: BORDER, bottom: BORDER, left: BORDER, right: BORDER };
const headerStyle = {
  font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: 'FFFFFFFF' } },
  fill: { patternType: 'solid', fgColor: { rgb: 'FF0D1D32' } },
  alignment: { vertical: 'center', wrapText: true },
  border,
};
const bodyStyle = { font: { name: 'Calibri', sz: 11 }, alignment: { vertical: 'top', wrapText: true }, border };
const MONEY_FORMAT = '£#,##0';
const PERCENT_FORMAT = '0%';

// A column is as wide as its longest cell, within reason.
function widths(headers, rows) {
  return headers.map((h, c) => {
    let w = String(h).length;
    for (const r of rows) w = Math.max(w, String(r[c] ?? '').length);
    return { wch: Math.min(60, Math.max(10, w + 2)) };
  });
}

// A number's format: pounds or a percentage by its column, else none. Text in
// those columns ("n/a", a blank) is left as it is.
function formatOf(sheet, c, v) {
  if (typeof v !== 'number') return undefined;
  if (sheet.money.includes(c)) return MONEY_FORMAT;
  if (sheet.percent.includes(c)) return PERCENT_FORMAT;
  return undefined;
}

/** Build the workbook as a list of sheets for xlsxWriter. Returned rather than written so a caller can inspect it. */
export function buildBidReportWorkbook(report) {
  return buildReportSheets(report).map(sheet => ({
    name: sheet.name,
    rows: [
      sheet.headers.map(v => ({ v, s: headerStyle })),
      ...sheet.rows.map(row => sheet.headers.map((_, c) => ({ v: row[c] ?? '', s: bodyStyle, z: formatOf(sheet, c, row[c]) }))),
    ],
    cols: widths(sheet.headers, sheet.rows),
    autofilter: sheet.rows.length ? `A1:${colName(sheet.headers.length - 1)}${sheet.rows.length + 1}` : undefined,
  }));
}

/** Build and download the report: `bid-report-<from>-to-<to>.xlsx`. */
export function downloadBidReport(report) {
  downloadXlsx(buildBidReportWorkbook(report), reportFileName(report.from, report.to));
}
